import { TokenManager, authenticatedFetch } from "@/supabase/supabaseClient";
import type { UserProfile } from "@/types/user";
import type { Role } from "@/types/rbac";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export class UserService {
  private static mapUserProfile(raw: Record<string, unknown>): UserProfile {
    const {
      id,
      email,
      first_name,
      last_name,
      created_at,
      updated_at,
      user_roles,
    } = raw as {
      id?: string;
      email?: string;
      first_name?: string;
      last_name?: string;
      created_at?: string;
      updated_at?: string;
      user_roles?: { roles?: Role }[];
    };

    const rbacRoles = Array.isArray(user_roles)
      ? user_roles
          .map((entry) => entry.roles)
          .filter((roleEntry): roleEntry is Role => Boolean(roleEntry))
      : [];

    return {
      id: id ?? "",
      email: email ?? "",
      first_name,
      last_name,
      created_at: created_at ?? new Date().toISOString(),
      updated_at: updated_at ?? new Date().toISOString(),
      rbac_roles: rbacRoles,
    };
  }

  /**
   * Fetch all user profiles belonging to a tenant (via tenant_members)
   */
  static async getAllUsers(tenantId: string): Promise<UserProfile[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch users");
        throw new Error("Authentication token not available");
      }

      const url = `${supabaseUrl}/rest/v1/tenant_members?select=user_id,user:user_id(id,email,first_name,last_name,created_at,updated_at,user_roles!user_roles_user_id_fkey(role_id,roles(id,name,description,is_system,is_active)))&tenant_id=eq.${tenantId}`;

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching users:", error);
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();
      const users = (data || []).map((tm: any) => this.mapUserProfile(tm.user));
      users.sort((a: UserProfile, b: UserProfile) =>
        (a.email ?? "").localeCompare(b.email ?? "")
      );
      return users;
    } catch (error) {
      console.error("Error in getAllUsers:", error);
      throw error;
    }
  }

  /**
   * Fetch all users including admins for a tenant.
   * Since tenant_members already includes all users regardless of role,
   * this delegates to getAllUsers.
   */
  static async getAllUsersIncludingAdmins(
    tenantId: string
  ): Promise<UserProfile[]> {
    return this.getAllUsers(tenantId);
  }

  /**
   * Get user profile by ID
   */
  static async getUserById(
    id: string,
    tenantId?: string
  ): Promise<UserProfile | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        console.log("❌ Token not ready, cannot fetch user by ID");
        throw new Error("Authentication token not available");
      }

      const params = new URLSearchParams({
        select:
          "id,email,first_name,last_name,created_at,updated_at,user_roles!user_roles_user_id_fkey(role_id,roles(id,name,description,is_system,is_active))",
        id: `eq.${id}`,
      });

      let url = `${supabaseUrl}/rest/v1/user_profiles?${params.toString()}`;
      if (tenantId) {
        url += `&user_roles.tenant_id=eq.${tenantId}`;
      }

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching user by ID:", error);
        return null;
      }

      const data = await response.json();
      return data && data.length > 0 ? this.mapUserProfile(data[0]) : null;
    } catch (error) {
      console.error("Error in getUserById:", error);
      return null;
    }
  }
}
